import { useEffect, useMemo, useRef, useState } from 'react';

import constants from 'constants/constants';
import { useSubscription } from 'react-stomp-hooks';
import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import useUserHook from 'hooks/useUserHook';

import { QueryResponse, useQueryGetProjectStatusScoreCards } from 'api/queries/getProjectStatusScoreCards.query';

import { FilterNames, getFiltersData } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';

import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';

export type UseScoreCardProvider = ReturnType<typeof useProjectSummaryProvider>;

interface Props {
    componentKey: string;
    widgetId?: string;
    latestVersionId?: number | undefined;
    compareVersionId?: number | undefined;
    globalFilterData: any;
    localFilters: any;
    contractId: number | null | undefined;
    endpoints: Record<string, string>;
}

export const useProjectSummaryProvider = ({
    widgetId,
    latestVersionId,
    localFilters,
    globalFilterData,
    compareVersionId,
    componentKey,
    contractId,
    endpoints,
}: Props) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const isInPinboard = Boolean(widgetId);
    const source = isInPinboard ? localFilters : { ...globalFilterData };
    const { email } = useUserHook();
    const [data, setData] = useState<null | QueryResponse>(null);
    const [networkStatus, setNetworkStatus] = useState<'idle' | 'loading' | 'success'>('idle');

    const filters = useMemo(() => {
        return getFiltersData({
            [FilterNames.ACTIVITY_CODES]: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.ACTIVITY_CODES_EXCLUDE]: source?.activityCodes
                ?.filter((item) => item.isExclude)
                .map((i) => i.name),
            [FilterNames.FRAGNET_SEARCH]: source?.milestoneFragnet,
            [FilterNames.TAG_CARD_BOARD]: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
            [FilterNames.TAG_CARD_BOARD_EXCLUDE]: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
            [FilterNames.WBS_SEARCH]: source?.wbs,
        });
    }, [source]);

    const getProjectStatusScoreCardsPayload = {
        compareVersionId,
        latestVersionId,
        contractId,
        filter: {
            filters,
        },
        widgetId,
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    };

    const getProjectStatusScoreCards = useQueryGetProjectStatusScoreCards(
        getProjectStatusScoreCardsPayload,
        endpoints.getProjectStatusScoreCards,
    );

    useEffect(() => {
        getProjectStatusScoreCards.status === 'loading' && setNetworkStatus('loading');
    }, [getProjectStatusScoreCards.status]);

    // create pill data for PinboardWidgetPills component
    const pillsData = {
        activityCodes: source?.activityCodes?.filter((item) => !item.isExclude).map((i) => i.name),
        activityCodesExclude: source?.activityCodes?.filter((item) => item.isExclude).map((i) => i.name),
        milestoneFragnet: source?.milestoneFragnet,
        tags: source?.tags?.filter((item) => !item.isExclude).map((i) => i.name),
        tagsExclude: source?.tags?.filter((item) => item.isExclude).map((i) => i.name),
        wbs: source?.wbs,
    };

    const { pillsList: pills } = useGlobalFiltersHook({ source: pillsData, latestVersionId });

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useSubscription(email ? [`/topic/${email}.notifications.push`] : [], (response) => {
        const { type, message } = JSON.parse(response.body);
        const key = isInPinboard ? widgetId : '-1';
        if (type === 'GetProjectStatusScoreCards') {
            if (message && String(key) === message.key) {
                setData(message.value);
                setNetworkStatus('success');
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
            }
        }
    });

    // if no response from wbe socket for more than 10 minutes - set network status success and leave data null so
    // "no data" message will be displayed to user
    useEffect(() => {
        if (networkStatus === 'loading') {
            setData(null);
            timeoutRef.current = setTimeout(() => {
                setData(null);
                setNetworkStatus('success');
            }, 10 * constants.MINUTE);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [networkStatus]);

    return {
        ui: {
            state: {
                activityCodes: source?.activityCodes,
                milestoneFragnet: source?.milestoneFragnet,
                tags: source?.tags,
                wbs: source?.wbs,
            },
            pills,
            isInPinboard,
            vars: {
                data,
                networkStatus,
            },
        },
        api: {
            getProjectStatusScoreCards: {
                instance: getProjectStatusScoreCards,
                payload: getProjectStatusScoreCardsPayload,
                endpoint: endpoints.getProjectStatusScoreCards,
            },
        },
    };
};
